import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppVisibility } from '@api/apps';
import {
  createPublicLink,
  createUserApp,
  deleteUserApp,
  duplicateUserApp,
  getPublicLink,
  getUserAppDraft,
  keepOptionsAsExample,
  listAppSkills,
  listCoverPresets,
  listMyApps,
  listUserAppVersions,
  promoteRunToExample,
  publishUserApp,
  restoreUserAppVersion,
  revokePublicLink,
  setUserAppVisibility,
  startTestRun,
  unshareUserApp,
  updateUserAppDraft,
  type DraftDetailsUpdate,
  type NewUserApp,
  type PublishRequest,
  type UserAppDraft,
} from '@api/appBuilder';
import { appKeys } from '../useApps';

export const appBuilderKeys = {
  mine: ['app-builder', 'mine'] as const,
  draft: (appKey: string) => ['app-builder', 'draft', appKey] as const,
  updateDraft: ['app-builder', 'update-draft'] as const,
  versions: (appKey: string) => ['app-builder', 'versions', appKey] as const,
  coverPresets: ['app-builder', 'cover-presets'] as const,
  skills: ['app-builder', 'skills'] as const,
  conversation: (appKey: string) => ['app-builder', 'conversation', appKey] as const,
  thumbnailOffer: ['app-builder', 'thumbnail-offer'] as const,
  thumbnail: (appKey: string) => ['app-builder', 'thumbnail', appKey] as const,
  publicLink: (appKey: string) => ['app-builder', 'public-link', appKey] as const,
};

export const useMyApps = (enabled: boolean) =>
  useQuery({
    queryKey: appBuilderKeys.mine,
    queryFn: listMyApps,
    enabled,
  });

export const useUserAppDraft = (appKey: string | undefined, enabled = true) =>
  useQuery({
    queryKey: appBuilderKeys.draft(appKey ?? 'none'),
    queryFn: () => getUserAppDraft(appKey as string),
    enabled: Boolean(appKey) && enabled,
  });

export const useUserAppVersions = (appKey: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: appBuilderKeys.versions(appKey ?? 'none'),
    queryFn: () => listUserAppVersions(appKey as string),
    enabled: Boolean(appKey) && enabled,
  });

export const usePublicLink = (appKey: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: appBuilderKeys.publicLink(appKey ?? 'none'),
    queryFn: () => getPublicLink(appKey as string),
    enabled: Boolean(appKey) && enabled,
  });

export const useCoverPresets = () =>
  useQuery({
    queryKey: appBuilderKeys.coverPresets,
    queryFn: listCoverPresets,
    staleTime: Infinity,
  });

export const useAppSkills = () =>
  useQuery({
    queryKey: appBuilderKeys.skills,
    queryFn: listAppSkills,
    staleTime: 5 * 60 * 1000,
  });

interface VisibilityVariables {
  readonly appKey: string;
  readonly visibility: AppVisibility;
}

interface DraftUpdateVariables {
  readonly appKey: string;
  readonly update: DraftDetailsUpdate;
}

interface PublishVariables {
  readonly appKey: string;
  readonly request: PublishRequest;
}

interface RestoreVariables {
  readonly appKey: string;
  readonly version: number;
  readonly expectedLockVersion: number;
}

interface PromoteExampleVariables {
  readonly appKey: string;
  readonly runId: string;
}

interface KeepOptionsVariables {
  readonly appKey: string;
  readonly options: Readonly<Record<string, unknown>>;
}

/**
 * Every change to a user app. Whatever can alter what the gallery or a run
 * resolves refreshes the catalog as well as the builder's own lists.
 */
export const useAppBuilderActions = () => {
  const queryClient = useQueryClient();

  const refresh = async (appKey?: string) => {
    const keys: (readonly unknown[])[] = [appBuilderKeys.mine, appKeys.catalog];
    if (appKey) {
      keys.push(appBuilderKeys.draft(appKey), appBuilderKeys.versions(appKey), appBuilderKeys.publicLink(appKey));
    }
    await Promise.all(
      keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }).catch(() => undefined)),
    );
  };

  const seedDraft = (draft: UserAppDraft) => {
    queryClient.setQueryData(appBuilderKeys.draft(draft.appKey), draft);
  };

  const create = useMutation({
    mutationFn: (details: NewUserApp | void) => createUserApp(details ?? undefined),
    onSuccess: (draft) => {
      seedDraft(draft);
      refresh().catch(() => undefined);
    },
  });

  const duplicate = useMutation({
    mutationFn: (appKey: string) => duplicateUserApp(appKey),
    onSuccess: (draft) => {
      seedDraft(draft);
      refresh().catch(() => undefined);
    },
  });

  const updateDraft = useMutation({
    mutationKey: appBuilderKeys.updateDraft,
    mutationFn: ({ appKey, update }: DraftUpdateVariables) => updateUserAppDraft(appKey, update),
    onSuccess: (draft) => {
      seedDraft(draft);
      queryClient.invalidateQueries({ queryKey: appBuilderKeys.mine }).catch(() => undefined);
    },
  });

  // Returned, not fired: Publish stays busy until the draft says what is live, so a second click can't slip in.
  const publish = useMutation({
    mutationFn: ({ appKey, request }: PublishVariables) => publishUserApp(appKey, request),
    onSuccess: (_result, { appKey }) => refresh(appKey),
  });

  const restoreVersion = useMutation({
    mutationFn: ({ appKey, version, expectedLockVersion }: RestoreVariables) =>
      restoreUserAppVersion(appKey, version, expectedLockVersion),
    onSuccess: (draft) => {
      seedDraft(draft);
      refresh(draft.appKey).catch(() => undefined);
    },
  });

  const setVisibility = useMutation({
    mutationFn: ({ appKey, visibility }: VisibilityVariables) =>
      setUserAppVisibility(appKey, visibility),
    onSuccess: (_data, { appKey }) => refresh(appKey),
  });

  const unshare = useMutation({
    mutationFn: (appKey: string) => unshareUserApp(appKey),
    onSuccess: (_data, appKey) => refresh(appKey),
  });

  const newPublicLink = useMutation({
    mutationFn: (appKey: string) => createPublicLink(appKey),
    onSuccess: (state, appKey) => {
      queryClient.setQueryData(appBuilderKeys.publicLink(appKey), state);
      refresh(appKey).catch(() => undefined);
    },
  });

  const turnOffPublicLink = useMutation({
    mutationFn: (appKey: string) => revokePublicLink(appKey),
    onSuccess: (_data, appKey) => refresh(appKey),
  });

  const testRun = useMutation({
    mutationFn: (appKey: string) => startTestRun(appKey),
  });

  const promoteExample = useMutation({
    mutationFn: ({ appKey, runId }: PromoteExampleVariables) => promoteRunToExample(appKey, runId),
    onSuccess: (draft) => {
      seedDraft(draft);
      queryClient.invalidateQueries({ queryKey: appBuilderKeys.mine }).catch(() => undefined);
    },
  });

  const keepOptions = useMutation({
    mutationFn: ({ appKey, options }: KeepOptionsVariables) => keepOptionsAsExample(appKey, options),
    onSuccess: (draft) => {
      seedDraft(draft);
      queryClient.invalidateQueries({ queryKey: appBuilderKeys.mine }).catch(() => undefined);
    },
  });

  const remove = useMutation({
    mutationFn: (appKey: string) => deleteUserApp(appKey),
    onSuccess: (_data, appKey) => {
      queryClient.removeQueries({ queryKey: appBuilderKeys.draft(appKey) });
      refresh().catch(() => undefined);
    },
  });

  return {
    create: create.mutateAsync,
    isCreating: create.isPending,
    duplicate: duplicate.mutateAsync,
    isDuplicating: duplicate.isPending,
    updateDraft: updateDraft.mutateAsync,
    isSavingDraft: updateDraft.isPending,
    publish: publish.mutateAsync,
    isPublishing: publish.isPending,
    restoreVersion: restoreVersion.mutateAsync,
    isRestoringVersion: restoreVersion.isPending,
    setVisibility: setVisibility.mutateAsync,
    isChangingVisibility: setVisibility.isPending,
    unshare: unshare.mutateAsync,
    createPublicLink: newPublicLink.mutateAsync,
    revokePublicLink: turnOffPublicLink.mutateAsync,
    isChangingPublicLink: newPublicLink.isPending || turnOffPublicLink.isPending,
    startTestRun: testRun.mutateAsync,
    promoteExample: promoteExample.mutateAsync,
    isPromotingExample: promoteExample.isPending,
    keepOptionsAsExample: keepOptions.mutateAsync,
    isKeepingOptions: keepOptions.isPending,
    remove: remove.mutateAsync,
  };
};
